import Head from 'next/head';
import { organization } from 'lib/seo/constants';
import sanitizeHtml from 'sanitize-html';

interface IOrganizationSchemaProps {
  name: string;
  url: string;
  logoImgUrl: string;
  legalName?: string;
  slogan?: string;
  description?: string;
  alternateNames?: string[];
  telephone?: string;
  address?: {
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    streetAddress: string;
  };
  mainEntityOfPage?: string;
  sameAsSites?: string[];
  awards?: string[];
  founders?: string[];
  foundingDate?: string;
  socialMediaSites?: string[];
}

export default function OrganizationSchema({
  name,
  url,
  logoImgUrl,
  legalName,
  slogan,
  description,
  alternateNames,
  telephone,
  address,
  mainEntityOfPage,
  sameAsSites,
  awards,
  founders,
  foundingDate,
  socialMediaSites,
}: IOrganizationSchemaProps) {
  const schemaOrg = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo: logoImgUrl,
    legalName,
    slogan,
    description,
    alternateName: alternateNames,
    sameAs: [...(sameAsSites ?? []), ...(socialMediaSites ?? [])],
    mainEntityOfPage,
    award: awards,
    ...(address && {
      address: {
        '@type': 'PostalAddress',
        ...address,
      },
    }),
    telephone,
    founder: founders,
    foundingDate,
    parentOrganization: {
      '@type': 'Organization',
      name: organization,
    },
  };

  const replacer = (_key: string, value: string | null): string | undefined => {
    if (value === null) {
      // Filter out null values in JSON by setting them to undefined
      return undefined;
    } else if (typeof value === 'string') {
      // Remove any HTML from strings
      return sanitizeHtml(value, { allowedTags: [], allowedAttributes: {} });
    } else {
      return value;
    }
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg, replacer) }}
      />
    </Head>
  );
}
